import { FieldName } from "@shopify/address";
import { APIProvider } from "@vis.gl/react-google-maps";
/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import React from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { AddressAutocomplete } from "~/components/ui/address-autocomplete";
import { cn } from "~/components/ui/cn";
import { CountryCombobox } from "~/components/ui/country-combobox";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { GoogleAPILoader } from "~/components/ui/google-api-loader";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { useAddressOrderedFieldsQuery } from "~/hooks/use-address-ordered-fields-query";
import { useCountriesQuery } from "~/hooks/use-countries-query";

export type AddressFormFieldsProps = {
  /**
   * Should the company address field be displayed?
   */
  showCompany: boolean;
  /**
   * Should the first name and last name address fields be displayed?
   */
  showName: boolean;
  /**
   * Should the phone address field be displayed?
   */
  showPhone: boolean;
  autoFocus?: boolean;
  disabledCountry?: boolean;
  countryRestrictions?: string[];
  locale: string;
  translations: {
    suggestions: string;
    fieldsNameValidationErrorsRequired: string;
    fieldsNameValidationErrorsMaxLength: string;
    fieldsFirstnameValidationErrorsRequired: string;
    fieldsFirstnameValidationErrorsMaxLength: string;
    fieldsLastnameValidationErrorsRequired: string;
    fieldsLastnameValidationErrorsMaxLength: string;
    fieldsPhoneValidationErrorsRequired: string;
    fieldsPhoneValidationErrorsMaxLength: string;
    fieldsStreetValidationErrorsRequired: string;
    fieldsStreetValidationErrorsMaxLength: string;
    fieldsStreet2ValidationErrorsMaxLength: string;
    fieldsBpValidationErrorsRequired: string;
    fieldsBpValidationErrorsMaxLength: string;
    fieldsCityValidationErrorsRequired: string;
    fieldsCityValidationErrorsMaxLength: string;
    fieldsProvinceValidationErrorsRequired: string;
    fieldsProvinceValidationErrorsMaxLength: string;
    fieldsCountryValidationErrorsRequired: string;
    fieldsNameLabel: string;
    fieldsNamePlaceholder: string;
    fieldsFirstnameLabel: string;
    fieldsFirstnamePlaceholder: string;
    fieldsLastnameLabel: string;
    fieldsLastnamePlaceholder: string;
    fieldsPhoneLabel: string;
    fieldsPhonePlaceholder: string;
    fieldsStreetLabel: string;
    fieldsStreetPlaceholder: string;
    fieldsStreet2Label: string;
    fieldsStreet2Placeholder: string;
    fieldsBpLabel: string;
    fieldsBpPlaceholder: string;
    fieldsCityLabel: string;
    fieldsCityPlaceholder: string;
    fieldsProvinceLabel: string;
    fieldsProvincePlaceholder: string;
    fieldsProvinceEmptyField: string;
    fieldsCountryLabel: string;
    fieldsCountryPlaceholder: string;
    fieldsCountryPlaceholderSearch: string;
    fieldsCountryEmptyField: string;
    fieldsCountryNoResults: string;
  };
  formPrefix?: string;
};

export const AddressFormFields: React.FC<AddressFormFieldsProps> = ({
  showCompany,
  showName,
  showPhone,
  autoFocus,
  disabledCountry,
  countryRestrictions,
  locale,
  translations,
  formPrefix,
}: AddressFormFieldsProps) => {
  const { control, setValue } = useFormContext();

  const getFormToken = (value: string) =>
    formPrefix ? `${formPrefix}.${value}` : value;

  const watchedCountryCode = useWatch({
    control: control,
    name: getFormToken("countryCode"),
  });

  const { data: countries } = useCountriesQuery({
    locale: locale,
  });
  const { data: addressOrderedFieldsData } = useAddressOrderedFieldsQuery({
    countryCode: watchedCountryCode,
    locale: locale,
  });

  const selectedCountry = countries?.find(
    (country) =>
      country.code.toLowerCase() ===
      (watchedCountryCode as string).toLowerCase(),
  );

  const fieldsMapping = {
    [FieldName.Company]: showCompany ? (
      <FormField
        control={control}
        name={getFormToken("name")}
        rules={{ required: true, maxLength: 200 }}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{translations.fieldsNameLabel}</FormLabel>
            <FormControl>
              <Input
                type="text"
                placeholder={translations.fieldsNamePlaceholder}
                autoFocus={autoFocus}
                {...field}
              />
            </FormControl>
            <FormMessage match="required">
              {translations.fieldsNameValidationErrorsRequired}
            </FormMessage>
            <FormMessage match="maxLength">
              {translations.fieldsNameValidationErrorsMaxLength}
            </FormMessage>
          </FormItem>
        )}
      />
    ) : null,
    [FieldName.FirstName]: showName ? (
      <FormField
        control={control}
        name={getFormToken("firstname")}
        rules={{ required: true, maxLength: 200 }}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{translations.fieldsFirstnameLabel}</FormLabel>
            <FormControl>
              <Input
                type="text"
                placeholder={translations.fieldsFirstnamePlaceholder}
                {...field}
              />
            </FormControl>
            <FormMessage match="required">
              {translations.fieldsFirstnameValidationErrorsRequired}
            </FormMessage>
            <FormMessage match="maxLength">
              {translations.fieldsFirstnameValidationErrorsMaxLength}
            </FormMessage>
          </FormItem>
        )}
      />
    ) : null,
    [FieldName.LastName]: showName ? (
      <FormField
        control={control}
        name={getFormToken("lastname")}
        rules={{ required: true, maxLength: 200 }}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{translations.fieldsLastnameLabel}</FormLabel>
            <FormControl>
              <Input
                type="text"
                placeholder={translations.fieldsLastnamePlaceholder}
                {...field}
              />
            </FormControl>
            <FormMessage match="required">
              {translations.fieldsLastnameValidationErrorsRequired}
            </FormMessage>
            <FormMessage match="maxLength">
              {translations.fieldsLastnameValidationErrorsMaxLength}
            </FormMessage>
          </FormItem>
        )}
      />
    ) : null,
    [FieldName.Phone]: showPhone ? (
      <FormField
        control={control}
        name={getFormToken("phone")}
        rules={{ required: true, maxLength: 200 }}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{translations.fieldsPhoneLabel}</FormLabel>
            <FormControl>
              <Input
                type="tel"
                placeholder={translations.fieldsPhonePlaceholder}
                {...field}
              />
            </FormControl>
            <FormMessage match="required">
              {translations.fieldsPhoneValidationErrorsRequired}
            </FormMessage>
            <FormMessage match="maxLength">
              {translations.fieldsPhoneValidationErrorsMaxLength}
            </FormMessage>
          </FormItem>
        )}
      />
    ) : null,
    [FieldName.Address1]: (
      <FormField
        control={control}
        name={getFormToken("street")}
        rules={{ required: true, maxLength: 200 }}
        render={({ field, fieldState: { error } }) => (
          <FormItem>
            <FormLabel>{translations.fieldsStreetLabel}</FormLabel>
            <APIProvider
              apiKey={import.meta.env.REACT_APP_GOOGLE_MAPS_API_KEY as string}
              libraries={["places"]}
            >
              <GoogleAPILoader>
                <FormControl>
                  <AddressAutocomplete
                    invalid={!!error}
                    includedRegionCodes={
                      countryRestrictions ?? [watchedCountryCode]
                    }
                    onValueChange={(value) => {
                      const streetNumber = value.addressComponents?.find(
                        (component) =>
                          !!component.types.includes("street_number"),
                      )?.longText;

                      const route = value.addressComponents?.find(
                        (component) => !!component.types.includes("route"),
                      )?.longText;

                      setValue(
                        getFormToken("street"),
                        [streetNumber, route].filter(Boolean).join(" "),
                      );

                      const postalCode = value.addressComponents?.find(
                        (component) =>
                          !!component.types.includes("postal_code"),
                      )?.longText;
                      setValue(getFormToken("bp"), postalCode ?? "", {
                        shouldValidate: true,
                        shouldDirty: true,
                      });

                      const city = value.addressComponents?.find(
                        (component) => !!component.types.includes("locality"),
                      )?.longText;
                      setValue(getFormToken("city"), city ?? "", {
                        shouldValidate: true,
                        shouldDirty: true,
                      });

                      const state = value.addressComponents?.find(
                        (component) =>
                          !!component.types.includes(
                            "administrative_area_level_1",
                          ),
                      )?.shortText;
                      setValue(getFormToken("province"), state ?? "", {
                        shouldValidate: true,
                        shouldDirty: true,
                      });

                      const countryCode = value.addressComponents?.find(
                        (component) => !!component.types.includes("country"),
                      )?.shortText;
                      setValue(getFormToken("countryCode"), countryCode ?? "", {
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                    }}
                    {...field}
                  />
                </FormControl>
              </GoogleAPILoader>
            </APIProvider>
            <FormMessage match="required">
              {translations.fieldsStreetValidationErrorsRequired}
            </FormMessage>
            <FormMessage match="maxLength">
              {translations.fieldsStreetValidationErrorsMaxLength}
            </FormMessage>
          </FormItem>
        )}
      />
    ),
    [FieldName.Address2]: (
      <FormField
        control={control}
        name={getFormToken("street2")}
        rules={{ maxLength: 200 }}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{translations.fieldsStreet2Label}</FormLabel>
            <FormControl>
              <Input
                type="text"
                placeholder={translations.fieldsStreet2Placeholder}
                autoComplete="address-line2"
                {...field}
              />
            </FormControl>
            <FormMessage match="maxLength">
              {translations.fieldsStreet2ValidationErrorsMaxLength}
            </FormMessage>
          </FormItem>
        )}
      />
    ),
    [FieldName.PostalCode]: (
      <FormField
        control={control}
        name={getFormToken("bp")}
        rules={{
          maxLength: 200,
        }}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{translations.fieldsBpLabel}</FormLabel>
            <FormControl>
              <Input
                type="text"
                autoComplete="postal-code"
                placeholder={translations.fieldsBpPlaceholder}
                {...field}
              />
            </FormControl>
            <FormMessage match="required">
              {translations.fieldsBpValidationErrorsRequired}
            </FormMessage>
            <FormMessage match="maxLength">
              {translations.fieldsBpValidationErrorsMaxLength}
            </FormMessage>
          </FormItem>
        )}
      />
    ),
    [FieldName.City]: (
      <FormField
        control={control}
        name={getFormToken("city")}
        rules={{
          required: true,
          maxLength: 200,
        }}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{translations.fieldsCityLabel}</FormLabel>
            <FormControl>
              <Input
                type="text"
                autoComplete="address-level2"
                placeholder={translations.fieldsCityPlaceholder}
                {...field}
              />
            </FormControl>
            <FormMessage match="required">
              {translations.fieldsCityValidationErrorsRequired}
            </FormMessage>
            <FormMessage match="maxLength">
              {translations.fieldsCityValidationErrorsMaxLength}
            </FormMessage>
          </FormItem>
        )}
      />
    ),
    [FieldName.Zone]: (
      <FormField
        control={control}
        name={getFormToken("province")}
        rules={{
          maxLength: 200,
        }}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{translations.fieldsProvinceLabel}</FormLabel>

            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue
                    placeholder={translations.fieldsProvinceEmptyField}
                  />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {selectedCountry?.zones?.map((option) => (
                  <SelectItem value={option.code} key={option.code}>
                    {option.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <FormMessage match="required">
              {translations.fieldsProvinceValidationErrorsRequired}
            </FormMessage>
            <FormMessage match="maxLength">
              {translations.fieldsProvinceValidationErrorsRequired}
            </FormMessage>
          </FormItem>
        )}
      />
    ),
    [FieldName.Country]: (
      <FormField
        control={control}
        name={getFormToken("countryCode")}
        rules={{
          required: true,
        }}
        render={({ field }) => {
          return (
            <FormItem className="grid gap-2">
              <FormLabel>{translations.fieldsCountryLabel}</FormLabel>
              <FormControl>
                <CountryCombobox disabled={disabledCountry} {...field} />
              </FormControl>
              <FormMessage match="required">
                {translations.fieldsCountryValidationErrorsRequired}
              </FormMessage>
            </FormItem>
          );
        }}
      />
    ),
  };

  return (
    <div className="space-y-6">
      {addressOrderedFieldsData?.map((addressFields) =>
        addressFields.length > 1 &&
        addressFields.map((af) => fieldsMapping[af]).filter((af) => !!af)
          .length > 0 ? (
          <div
            className={cn(
              `grid grid-cols-1 space-y-6 sm:gap-x-6 sm:space-y-0`,
              {
                "grid-cols-2": addressFields.length === 2,
                "grid-cols-3": addressFields.length === 3,
              },
            )}
            key={addressFields.join("-")}
          >
            {addressFields.map((addressField) => (
              <div key={`${addressFields.join("-")}-${addressField}`}>
                {fieldsMapping[addressField]}
              </div>
            ))}
          </div>
        ) : addressFields[0] && fieldsMapping[addressFields[0]] ? (
          <div key={addressFields[0]}>{fieldsMapping[addressFields[0]]}</div>
        ) : null,
      )}
    </div>
  );
};
